import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import config from "../config";
import "./Search.css";

const Search = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // Function to highlight search terms in the message
  const highlightSearchTerms = (text, query) => {
    if (!query || !text) return text;

    // Split query into individual words and escape special regex characters
    const terms = query
      .split(/\s+/)
      .filter(term => term.length > 0)
      .map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    if (terms.length === 0) return text;

    // Create a regex pattern that matches any of the terms (case-insensitive)
    const pattern = new RegExp(`(${terms.join('|')})`, 'gi');

    // Split text by matches and wrap matches in spans
    const parts = text.split(pattern);
    
    return parts.map((part, index) => {
      // Check if this part matches any search term
      const isMatch = terms.some(term => 
        new RegExp(`^${term}$`, 'i').test(part)
      );
      
      if (isMatch) {
        return (
          <mark key={index} className="search-highlight">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const handleCardClick = (result) => {
    // Navigate to the chat page with the chatbot ID, history ID, and message info for scrolling
    const searchParams = new URLSearchParams({
      id: result.chatbot_id,
      historyId: result.chat_history_id,
      messageTimestamp: result.timestamp,
      searchQuery: searchQuery // Pass the search query for highlighting
    });
    navigate(`/chat?${searchParams.toString()}`);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setError("Please enter a search term");
      return;
    }

    setIsSearching(true);
    setError("");
    setHasSearched(true);

    try {
      // TODO: Implement actual search API endpoint
      const response = await fetch(`${config.api.baseUrl}/chatbot/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          query: searchQuery,
          user_id: user?._id || user?.email,
        }),
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.message || "Failed to perform search. Please try again.");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (error) setError("");
  };

  return (
    <Layout>
      <div className="search-container">
        <div className="search-header">
          <p className="search-description">
            Search through all your chat conversations across all chatbots
          </p>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Enter keywords or phrases to search..."
              value={searchQuery}
              onChange={handleInputChange}
              disabled={isSearching}
            />
            <button
              type="submit"
              className="search-button"
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? "Searching..." : "Search"}
            </button>
          </div>
          {error && <div className="search-error">{error}</div>}
        </form>

        <div className="search-results">
          {isSearching && (
            <div className="search-loading">
              <div className="search-spinner"></div>
              <p>Searching through your chat history...</p>
            </div>
          )}

          {!isSearching && hasSearched && searchResults.length === 0 && (
            <div className="search-no-results">
              <p>No results found for "{searchQuery}"</p>
              <p className="search-no-results-hint">
                Try using different keywords or check your spelling
              </p>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && (
            <>
              <div className="search-results-header">
                <h2>
                  Found {searchResults.length} result
                  {searchResults.length !== 1 ? "s" : ""}
                </h2>
              </div>
              <div className="search-results-list">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="search-result-card"
                    onClick={() => handleCardClick(result)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick(result);
                      }
                    }}
                  >
                    <div className="search-result-card-header">
                      <div className="search-result-card-info">
                        <div className="search-result-chatbot-name">
                          {result.chatbot_name || "Unknown Chatbot"}
                        </div>
                        <div className="search-result-conversation-title">
                          {result.title || "Untitled Conversation"}
                        </div>
                      </div>
                      <div className="search-result-date">
                        {result.timestamp
                          ? new Date(result.timestamp).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : "Unknown date"}
                      </div>
                    </div>
                    <div className="search-result-card-body">
                      <p className="search-result-message">
                        {highlightSearchTerms(
                          result.message_preview || result.message,
                          searchQuery
                        )}
                      </p>
                      <div className="search-result-card-footer">
                        <span className="search-result-view-link">
                          View conversation
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M5 12h14"></path>
                            <path d="m12 5 7 7-7 7"></path>
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!isSearching && !hasSearched && (
            <div className="search-empty-state">
              <svg
                className="search-empty-icon"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <p>Enter a search term to find messages in your chat history</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Search;
